import React from "react";
import { Spinner } from "../../atoms/Spinner/Spinner.js";
import type { EmptyStateSize } from "../EmptyState/EmptyState.js";
import type { AsyncStatus } from "./asyncStatus.js";
import { SectionError } from "./SectionError.js";
import { cn } from "../../lib/utils.js";

/* ---------------- AsyncSection ----------------

   The four states of a region that loads something, in one place, so a surface
   cannot render three of them and forget the fourth.

   Everything the console needs already existed — `EmptyState` has the error
   variant and an action slot, and Settings → Account composes them correctly.
   What did not exist was anywhere to put that composition, so twenty-odd
   surfaces each answered the question again and the console ended up saying
   the same thing four ways: bare red text, a red bar, a centred panel with no
   way back, and a centred panel with a Retry.

   Data-blind by construction (§ADR-0009): the status is a string and the
   failure is an `Error`. `packages/ui` learns nothing about TanStack Query,
   and `asyncStatus` (./asyncStatus.ts) is the only place the mapping lives. */

export interface AsyncSectionProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  status: AsyncStatus;
  /** The content, rendered only when `status` is `ready`. */
  children: React.ReactNode;
  /**
   * Shown while loading. Give it the SHAPE of what is coming — skeleton rows
   * for a table, blocks for a form — so the region does not resize when the
   * data lands.
   */
  skeleton?: React.ReactNode;
  /**
   * Visible words for a wait with no `skeleton` — "Connecting to workshop…" —
   * beside a small `aria-hidden` ring Spinner, inside the one `role="status"`
   * region, so no second live region exists and the name stays "Loading
   * {loadingLabel}". Changing the words re-announces them. Ignored when
   * `skeleton` is given.
   */
  loadingMessage?: React.ReactNode;
  /**
   * The trailing action group of that wait — "Exit workshop" — under the
   * message and in the Tab order. Ignored when `skeleton` is given.
   */
  loadingActionsSlot?: React.ReactNode;
  /** Shown when the region settled with nothing in it. An `EmptyState`. */
  empty?: React.ReactNode;
  /**
   * What failed, in the reader's terms — "Couldn't load the OAuth clients",
   * not the server's sentence. Required, because a failure that does not name
   * its subject tells a reader a service is unavailable without telling them
   * which part of the page is missing.
   */
  errorTitle: string;
  /** The detail under it. The server's own message belongs here, not above. */
  errorDescription?: React.ReactNode;
  /**
   * Accessible name of the loading region — "the OAuth clients", so the wait
   * announces as "Loading the OAuth clients" and a reader knows WHICH part of
   * the page is busy.
   *
   * Named explicitly rather than derived from `errorTitle`. Deriving it meant
   * stripping a leading "Couldn't load", and the repo writes that with both a
   * straight and a typographic apostrophe — so on the nine surfaces using the
   * curly one, nothing stripped and a SUCCESSFUL load announced itself as
   * "Loading — Couldn't load the OAuth clients".
   */
  loadingLabel?: string;
  /** Wire this to the query's `refetch`. Without it there is no Retry. */
  onRetry?: () => void;
  retryLabel?: string;
  /**
   * The error branch only: `sm` reports the failure as a one-line row with a
   * small Retry, for a region too small for the centred panel. Default `md`.
   */
  size?: EmptyStateSize;
}

/**
 * Renders exactly one of skeleton, error, empty or content.
 *
 * The branch order is the component's, not the caller's, because the caller
 * getting it wrong is the defect: rendering the skeleton on `!data` rather
 * than on `isLoading` leaves a failed query showing a skeleton for ever, which
 * is what three Settings surfaces do today.
 */
export function AsyncSection({
  status,
  children,
  skeleton,
  loadingMessage,
  loadingActionsSlot,
  empty,
  errorTitle,
  loadingLabel,
  errorDescription,
  onRetry,
  retryLabel = "Retry",
  size = "md",
  className,
  ...rest
}: AsyncSectionProps) {
  /* Each branch is KEYED on the status (ADR-0091). The four branches occupy one
     position, so without a key React reconciles them as the same element, the
     @starting-style arrival never re-triggers, and a skeleton becomes a table in
     a single frame. The key makes the swap a remount, so it fades in. */
  if (status === "error") {
    return (
      <div
        key="error"
        className={cn("pb-motion-content-enter", className)}
        {...rest}
      >
        <SectionError
          title={errorTitle}
          description={errorDescription}
          {...(onRetry ? { onRetry } : {})}
          retryLabel={retryLabel}
          size={size}
        />
      </div>
    );
  }

  if (status === "loading") {
    /* `role="status"` and a name, so the wait is announced rather than being a
       silent rearrangement of the page. `aria-busy` is what tells assistive
       tech the region is mid-update — around a skeleton only: a busy live
       region is not announced (Chromium fires no live-region event while it
       is true, and this region unmounts rather than going false), so a
       message that must re-announce when its words change cannot sit in one.
       The message frame matches the error panel's, so a failed wait does not
       jump. */
    const hasMessage =
      skeleton == null &&
      (loadingMessage != null || loadingActionsSlot != null);
    return (
      <div
        key="loading"
        role="status"
        aria-busy={hasMessage ? undefined : "true"}
        aria-label={loadingLabel ? `Loading ${loadingLabel}` : "Loading"}
        className={cn("pb-motion-content-enter", className)}
        {...rest}
      >
        {hasMessage ? (
          <div className="flex flex-col items-center text-center px-6 py-10 gap-3">
            <div className="inline-flex items-center gap-2 text-sm text-pb-text-muted">
              <Spinner variant="ring" size="sm" aria-hidden />
              {loadingMessage != null && <span>{loadingMessage}</span>}
            </div>
            {loadingActionsSlot != null && (
              <div className="mt-1 flex items-center gap-2">
                {loadingActionsSlot}
              </div>
            )}
          </div>
        ) : (
          skeleton
        )}
      </div>
    );
  }

  if (status === "empty") {
    return (
      <div
        key="empty"
        className={cn("pb-motion-content-enter", className)}
        {...rest}
      >
        {empty}
      </div>
    );
  }

  return (
    <div
      key="ready"
      className={cn("pb-motion-content-enter", className)}
      {...rest}
    >
      {children}
    </div>
  );
}
